import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, CalendarX2, Check, Undo2, CornerDownRight, Plus } from 'lucide-react'
import { api } from '../../lib/api.js'
import { fmtClock } from '../../lib/date.js'
import { toast } from '../../components/feedback/Toast.jsx'
import { fmtDay } from './lib.js'
import BackBar from './BackBar.jsx'

/**
 * Admin log check (Charles, 2026-08-04): the last 14 days' room bookings
 * that don't loosely match any logged session — the accountability view.
 * Advisory on purpose: booking titles drift from session titles, so this
 * says "possibly unlogged" and shows what WAS logged that day for a quick
 * human verdict. The verdict is recordable (Charles, 2026-08-05): the
 * checkmark dismisses a flagged booking ("it was logged, the name just
 * drifted"), which stops it counting toward the day's deficit; days whose
 * titles all match but still under-count get "Mark day as reviewed".
 * Dismissals live server-side and are undoable below. Admin-only; the
 * server answers everyone else with an empty list.
 */
export default function LogCheckPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [refreshing, setRefreshing] = useState(false)
  const [busyKey, setBusyKey] = useState(null)
  const { data, isLoading } = useQuery({
    queryKey: ['sl-check'],
    queryFn: () => api('/log/check'),
    refetchOnWindowFocus: true,
  })

  async function refresh() {
    setRefreshing(true)
    try {
      qc.setQueryData(['sl-check'], await api('/log/check?fresh=1'))
    } catch {
      toast('Couldn’t reach the calendar', 'error')
    } finally {
      setRefreshing(false)
    }
  }

  // Log a flagged booking from here (Charles, 2026-09-20): the same tap a
  // suggestion gets — the new-session form, prefilled from the booking — but
  // dated the day the booking WAS, not today.
  function logIt(date, e) {
    const pf = e.prefill || { title: e.title, label: e.displayTitle || e.title }
    const qs = new URLSearchParams({ title: pf.title, label: pf.label || pf.title, date })
    if (pf.editGoesTo) qs.set('editGoesTo', pf.editGoesTo)
    if (pf.narrators) qs.set('narrators', pf.narrators)
    if (pf.room) qs.set('room', pf.room)
    if (pf.sessionType) qs.set('sessionType', pf.sessionType)
    navigate(`/log/new?${qs.toString()}`)
  }

  async function dismiss(date, key, label) {
    setBusyKey(`${date}|${key}`)
    try {
      await api('/log/check/dismiss', { body: { date, key, label } })
      await qc.invalidateQueries({ queryKey: ['sl-check'] })
    } catch (err) {
      toast(err.message || 'Couldn’t dismiss', 'error')
    } finally {
      setBusyKey(null)
    }
  }

  async function undismiss(date, key) {
    setBusyKey(`${date}|${key}`)
    try {
      await api('/log/check/undismiss', { body: { date, key } })
      await qc.invalidateQueries({ queryKey: ['sl-check'] })
    } catch (err) {
      toast(err.message || 'Couldn’t undo', 'error')
    } finally {
      setBusyKey(null)
    }
  }

  const days = data?.days || []
  const dismissed = data?.dismissed || []

  return (
    <div>
      <BackBar to="/log" label="Session Log" />

      <div className="mb-4 border-b border-hairline pb-3">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-ink">Log check</h1>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="tap flex items-center gap-1 text-xs font-medium text-ink underline underline-offset-2 disabled:opacity-50"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Checking…' : 'Refresh'}
          </button>
        </div>
        <p className="mt-1 text-sm text-muted">
          Bookings from the last two weeks whose titles match no logged session. A day
          in red logged fewer sessions than rooms were booked; a day whose counts
          balance stays here softly until each unmatched booking is checked off. Where a
          log looks like the same job under a different name it's offered underneath —
          worth opening before you decide. The checkmark dismisses a booking that was
          actually logged; the plus, or a tap on the booking, logs one that wasn't — the
          form opens prefilled and dated that day.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted">Checking the calendar…</p>
      ) : data?.unreachable ? (
        <p className="text-sm text-muted">The calendar didn’t answer — try Refresh.</p>
      ) : !data?.configured ? (
        <p className="text-sm text-muted">No calendar feeds configured.</p>
      ) : days.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted">
          All clear — every booked day in the last two weeks has at least as many logs as bookings.
        </p>
      ) : (
        <div className="space-y-6">
          {days.map((d) => (
            <section key={d.date}>
              {/* The day's own verdict, in colour (Charles, 2026-09-03 — the
                  text above has promised "a day in red" since 08-05 and
                  nothing ever drew it). Red is this page's sanctioned alarm,
                  same language as the badge dots: it means sessions are
                  genuinely missing. A day whose counts balance stays quiet —
                  it only needs its titles adjudicating. The colour goes on
                  spans because .section-header hard-sets the ink colour. */}
              <h2 className="section-header mb-2 flex flex-wrap items-center gap-x-1.5">
                {d.deficit > 0 && (
                  <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-danger" aria-hidden="true" />
                )}
                <span className={d.deficit > 0 ? 'text-danger' : undefined}>
                  {fmtDay(d.date)} · {d.booked} booked · {d.logged.length} logged
                </span>
                {d.deficit > 0 ? (
                  <span className="text-danger">
                    · {d.deficit} unlogged
                  </span>
                ) : (
                  <span className="font-normal text-faint">· counts balance</span>
                )}
              </h2>
              <div className="card divide-y divide-hairline">
                {d.deficit === 0 && d.missing.length > 0 && (
                  <p className="px-4 py-2.5 text-xs text-muted">
                    As many logs as bookings — these titles just don’t match any log.
                    Check off the ones that were logged under another name.
                  </p>
                )}
                {d.missing.length === 0 && (
                  <div className="flex items-center gap-3 px-4 py-3">
                    <p className="min-w-0 flex-1 text-sm text-muted">
                      Every booking's title matches a log — likely two bookings sharing one
                      session. {d.deficit} fewer log{d.deficit === 1 ? '' : 's'} than bookings.
                    </p>
                    <button
                      onClick={() => dismiss(d.date, '', 'Whole day')}
                      disabled={busyKey === `${d.date}|`}
                      className="tap shrink-0 text-xs font-medium text-ink underline underline-offset-2 disabled:opacity-50"
                    >
                      Mark day as reviewed
                    </button>
                  </div>
                )}
                {d.missing.map((e) => (
                  <div key={e.key} className="flex items-center gap-3 px-4 py-3">
                    <CalendarX2 size={16} className="shrink-0 text-faint" />
                    <div className="min-w-0 flex-1">
                      {/* The booking itself is the tap: it opens the new-session
                          form prefilled, like a suggestion (the + on the right
                          does the same, for anyone who doesn't guess that). */}
                      <button type="button" onClick={() => logIt(d.date, e)} className="block w-full text-left active:opacity-60">
                        <div className="truncate text-sm font-medium text-ink">
                          {[e.hold ? 'HOLD' : null, e.displayTitle ?? e.title].filter(Boolean).join(' - ')}
                        </div>
                        {/* Whose booking it was — the point of the check is
                            knowing who to ask (Charles, 2026-08-27). Blank for
                            bookings that only exist on the shared calendar. */}
                        <div className="mt-0.5 text-xs text-muted">
                          {[e.room, `${fmtClock(e.start)}–${fmtClock(e.end)}`, e.engineer]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </button>
                      {/* The likeliest log for this booking, where one looks
                          close enough (Charles, 2026-09-03) — so the verdict
                          doesn't depend on remembering what a session was
                          called. A guess, deliberately: it never clears the
                          flag, it just says which log to look at first. */}
                      {e.maybe && (
                        <Link
                          to={e.maybe.projectId ? `/log/${e.maybe.projectId}/s/${e.maybe.id}` : `/log/s/${e.maybe.id}`}
                          className="mt-1 flex items-start gap-1 text-xs text-navy"
                        >
                          <CornerDownRight size={12} className="mt-0.5 shrink-0" />
                          <span className="min-w-0">
                            Possibly logged as{' '}
                            <span className="font-medium underline underline-offset-2">
                              {e.maybe.label || e.maybe.project}
                            </span>
                            {e.maybe.project && e.maybe.label !== e.maybe.project && ` (${e.maybe.project})`}
                            {e.maybe.by && <span className="text-muted"> · {e.maybe.by}</span>}
                          </span>
                        </Link>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => logIt(d.date, e)}
                      className="tap flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-navy text-navy active:bg-grouped"
                      style={{ borderWidth: '1.5px' }}
                      aria-label={`Log it — ${e.title}`}
                      title="Not logged — log it now"
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      onClick={() => dismiss(d.date, e.key, e.title)}
                      disabled={busyKey === `${d.date}|${e.key}`}
                      className="tap flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-hairline text-muted active:bg-grouped disabled:opacity-50"
                      style={{ borderWidth: '1.5px' }}
                      aria-label={`Dismiss — ${e.title} was logged`}
                      title="It was logged — dismiss"
                    >
                      <Check size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-1.5 text-xs text-faint">
                {d.logged.length === 0
                  ? 'Nothing was logged that day.'
                  : 'Logged that day: ' +
                    d.logged
                      .map((s) => `${s.label || s.project || 'Session'}${s.by ? ` (${s.by})` : ''}`)
                      .join(' · ')}
              </p>
            </section>
          ))}
        </div>
      )}

      {dismissed.length > 0 && (
        <section className="mt-8">
          <h2 className="section-header mb-2">Dismissed</h2>
          <div className="card divide-y divide-hairline">
            {dismissed.map((v) => (
              <div key={`${v.date}|${v.key}`} className="flex items-center gap-3 px-4 py-2.5">
                <Check size={14} className="shrink-0 text-faint" />
                <div className="min-w-0 flex-1 text-sm text-muted">
                  {fmtDay(v.date)} · <span className="text-ink">{v.label || (v.key === '' ? 'Whole day' : v.key)}</span>
                </div>
                <button
                  onClick={() => undismiss(v.date, v.key)}
                  disabled={busyKey === `${v.date}|${v.key}`}
                  className="tap flex shrink-0 items-center gap-1 text-xs text-muted underline underline-offset-2 disabled:opacity-50"
                >
                  <Undo2 size={12} /> Undo
                </button>
              </div>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-faint">
            Dismissed bookings stop counting toward their day. Undo puts one back.
          </p>
        </section>
      )}
    </div>
  )
}
